/**
 * Re-encodes a picked clip to at most 720p H.264 so a phone-sized recording fits the review upload.
 *
 * Demuxed and decoded rather than played: `requestVideoFrameCallback` only hands over frames the
 * display actually presented, so capturing a 25s 30fps clip at 16x yields 187 of its 750 frames.
 */

import type { ConversionVideoOptions } from 'mediabunny';

const TARGET_HEIGHT = 720;

/** The encode aims below the ceiling because bitrate-driven encoding overshoots its target. */
const TARGET_BYTES = 9 * 1024 * 1024;

/** Audio and the container itself take a share of the budget the video must not spend. */
const VIDEO_BUDGET_SHARE = 0.94;
const AUDIO_BITRATE = 96_000;

const MIN_VIDEO_BITRATE = 200_000;
const MAX_VIDEO_BITRATE = 12_000_000;

const VIDEO_CODEC = 'avc';
const AUDIO_CODEC = 'aac';

/** On demand: no page needs a demuxer per container until a reviewer picks a clip. */
const loadMediabunny = () => import('mediabunny');

/** `VideoEncoder` existing is not the answer — Safari exposes it without H.264 — so ask the config. */
export const canEncodeReviewVideo = async (): Promise<boolean> => {
  if (typeof VideoEncoder !== 'function') {
    return false;
  }

  try {
    const { canEncodeVideo } = await loadMediabunny();
    return await canEncodeVideo(VIDEO_CODEC, { width: 1280, height: TARGET_HEIGHT });
  } catch {
    return false;
  }
};

const planVideo = (durationSeconds: number, sourceHeight: number): ConversionVideoOptions => {
  const budgetBits = TARGET_BYTES * 8 * VIDEO_BUDGET_SHARE;
  const bitrate = durationSeconds > 0 ? Math.round(budgetBits / durationSeconds) : MAX_VIDEO_BITRATE;

  return {
    // Width follows the source aspect ratio. `height` on its own has no ceiling against the source —
    // left at the target it upscales a shorter clip, spending a full encode to produce something
    // usually larger, which the caller then discards.
    height: Math.min(TARGET_HEIGHT, sourceHeight),
    codec: VIDEO_CODEC,
    bitrate: Math.max(MIN_VIDEO_BITRATE, Math.min(bitrate, MAX_VIDEO_BITRATE)),
  };
};

const toMp4FileName = (fileName: string): string => fileName.replace(/(\.[^.]*)?$/, '.mp4');

export interface EncodeReviewVideoOptions {
  onProgress?: (ratio: number) => void;
  /**
   * Closing the form mid-encode otherwise leaves the decoder/encoder pair and the demuxer running to
   * completion for a review nobody will submit — a real battery and thermal cost on a phone.
   */
  signal?: AbortSignal;
}

/** Returns the original when re-encoding would not help — already small, or the encode came out larger. */
export const encodeReviewVideo = async (file: File, options: EncodeReviewVideoOptions = {}): Promise<File> => {
  if (file.size <= TARGET_BYTES) {
    return file;
  }

  // Ahead of the import and the container parse: a queue of clips abandoned at once would otherwise
  // each pay for a full demux and encoder init only to be cancelled, which is the cost being avoided.
  if (options.signal?.aborted) {
    return file;
  }

  const { BlobSource, BufferTarget, Conversion, Input, Mp4OutputFormat, MP4, Output, QTFF, WEBM } =
    await loadMediabunny();

  // Only the containers the picker offers, so the unused demuxers stay out of the chunk.
  const input = new Input({ source: new BlobSource(file), formats: [MP4, QTFF, WEBM] });

  try {
    const output = new Output({
      // Puts the index at the front, so the clip starts playing before it has fully downloaded.
      format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
      target: new BufferTarget(),
    });

    // A container with no readable video track keeps the target, which is what the ceiling was for.
    const sourceTrack = await input.getPrimaryVideoTrack();
    const sourceHeight = sourceTrack ? await sourceTrack.getDisplayHeight() : TARGET_HEIGHT;

    const conversion = await Conversion.init({
      input,
      output,
      video: planVideo(await input.computeDuration(), sourceHeight),
      audio: { codec: AUDIO_CODEC, bitrate: AUDIO_BITRATE },
    });
    if (options.onProgress) {
      conversion.onProgress = options.onProgress;
    }

    // A cancel that fails itself must not surface as an unhandled rejection while the form unmounts.
    const cancelConversion = () => void conversion.cancel().catch(() => undefined);
    // Registered before the check below because an abort arriving during `execute` has no other
    // way in; the check itself only covers one that already landed during setup.
    options.signal?.addEventListener('abort', cancelConversion, { once: true });

    try {
      // Fired while the setup above was resolving, which the listener could not have seen. A
      // cancelled conversion can no longer execute, so this waits for the cancel and hands back the
      // original rather than awaiting an `execute` that will never produce anything.
      if (options.signal?.aborted) {
        await conversion.cancel().catch(() => undefined);
        return file;
      }

      await conversion.execute();
    } finally {
      options.signal?.removeEventListener('abort', cancelConversion);
    }

    const buffer = output.target.buffer;
    if (!buffer) {
      throw new Error('The video could not be written');
    }

    const encoded = new File([buffer], toMp4FileName(file.name), {
      type: 'video/mp4',
      lastModified: file.lastModified,
    });

    return encoded.size < file.size ? encoded : file;
  } finally {
    input.dispose();
  }
};
